import * as queryString from 'querystring'
import React, { useEffect, useState } from 'react'
import { connect, useSelector } from 'react-redux'
import { Redirect, withRouter } from 'react-router'
import { IStore } from '../../reducers'
import {
  getIsAuthorizing,
  isUserAuthorized,
} from '../../reducers/userAccount/selectors/credentials'
import { localStorage } from '../../database'
import { AuthorizationActions } from '../../reducers/userAccount/actionNames'
import getValidTime from '../../reducers/userAccount/selectors/getValidTime'
import DateError from '../../screens/DateError/DateError'
import useIsScope from '../../hooks/useIsScope'
import {
  getUserScope,
  isUserLoading,
} from '../../reducers/userAccount/selectors/userAccount'
import { FullScreenLoader } from '../../components/Loader'

const mapStateToProps = () => {
  const isAuth = isUserAuthorized

  return (state: IStore): IIsAuthorized => ({
    isAuthorized: isAuth(state),
    isAuthorizing: getIsAuthorizing(state),
    isValidDate: getValidTime(state),
    isUserLoading: isUserLoading(state),
  })
}

const mapDispatchToProps = dispatch => ({
  tryAuth: (username, accessToken = undefined) =>
    dispatch({
      accessToken,
      type: AuthorizationActions.RequestAuthorization,
      username,
    }),
})

function Protected(redirectPath: string) {
  const props = mapStateToProps()
  const c = connect(
    props,
    mapDispatchToProps,
  )

  return function(Component) {
    function Renderer({
      isAuthorized,
      isUserLoading,
      isAuthorizing,
      tryAuth,
      isValidDate,
      location,
      ...rest
    }: IIsAuthorized & any) {
      let { accessToken, mobile } = queryString.parse(
        location.search.replace('?', ''),
      )
      const scopeTimeoutRef = React.useRef<number | null>(null)
      const [userScopes, setUserScope] = useState(undefined)
      let scop = useSelector(getUserScope)
      let allowed = useIsScope(Component.scope)

      mobile = mobile
        ? mobile
        : location.state && location.state.mobile
        ? '1'
        : undefined

      useEffect(() => {
        setUserScope(scop)

        if (scopeTimeoutRef.current) {
          clearTimeout(scopeTimeoutRef.current)
          scopeTimeoutRef.current = null
        }

        // https://bed-booking.atlassian.net/browse/BED-39312
        if (mobile === '1') return

        if (scop === undefined) {
          scopeTimeoutRef.current = setTimeout(() => {
            setUserScope([])
          }, 5000)
        }

        return () => {
          if (scopeTimeoutRef.current) {
            clearTimeout(scopeTimeoutRef.current)
            scopeTimeoutRef.current = null
          }
        }
      }, [mobile, scop])

      allowed = Component.scope ? allowed : true

      useEffect(() => {
        if (accessToken) {
          tryAuth('MobileViews', accessToken)
        } else if (!isAuthorized) {
          const { Driver, Names } = localStorage
          const { CredentialsStorage } = Names
          const username = Driver.getItem(CredentialsStorage.UserID, '')
          tryAuth(username)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [])

      if (isValidDate) {
        if (isAuthorizing || isUserLoading || !userScopes) {
          return (
            <FullScreenLoader
              style={{
                height: '100vh',
              }}
            />
          )
        }
        if (isAuthorized) {
          if (allowed) {
            return <Component.C mobile={mobile} location={location} {...rest} />
          } else {
            return (
              <Redirect
                to={{
                  pathname: '/403',
                  state: {
                    from:
                      location.pathname +
                      (location.search ? location.search : ''),
                  },
                }}
              />
            )
          }
        }
        return (
          <Redirect
            to={{
              pathname: redirectPath,
              state: {
                from:
                  location.pathname + (location.search ? location.search : ''),
              },
            }}
          />
        )
      } else {
        return <DateError />
      }
    }

    return c(withRouter(Renderer))
  }
}

export default Protected

export interface IIsAuthorized {
  isAuthorized?: boolean
  isAuthorizing?: boolean
  isValidDate?: boolean
  isUserLoading?: boolean
  tryAuth?: (username) => void
}
